import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000; // 30s
const SEEN_KEY = 'gmail_last_seen_ids_v1';

const readSeen = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]')); }
  catch { return new Set(); }
};
const writeSeen = (set) => {
  try { sessionStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-200))); } catch { /* ignore */ }
};

const cleanSender = (from) => {
  if (!from) return '';
  const m = from.match(/^"?([^"<]+?)"?\s*<.+>$/);
  return (m ? m[1] : from).trim();
};

/**
 * Polls Gmail inbox for unread count + detects newly received unread messages.
 * Fires a toast + browser Notification on new arrivals (after the initial fetch).
 * Returns the live unread count (used for the sidebar badge).
 */
export const useGmailBadge = (enabled) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const seenIdsRef = useRef(readSeen());
  const initialLoadRef = useRef(true);

  useEffect(() => {
    if (!enabled) { setUnreadCount(0); return; }
    let mounted = true;

    // Politely request browser notification permission once
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch { /* ignore */ }

    const fireNewEmail = (m) => {
      const sender = cleanSender(m.from) || m.from || 'Inconnu';
      const subj = m.subject || '(sans objet)';
      // In-app toast
      toast.info(`Nouvel email - ${sender}`, {
        description: subj.length > 80 ? subj.slice(0, 80) + '...' : subj,
        duration: 8000,
        action: { label: 'Voir', onClick: () => { window.location.href = '/gmail'; } },
      });
      // Browser-level notification
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          const n = new Notification(`Nouvel email - ${sender}`, {
            body: subj,
            icon: '/favicon.ico',
            tag: m.id,
            silent: false,
          });
          n.onclick = () => { try { window.focus(); } catch { /* ignore */ } window.location.href = '/gmail'; n.close(); };
        }
      } catch { /* ignore */ }
    };

    const poll = async () => {
      try {
        const [labelsR, msgsR] = await Promise.all([
          axios.get(`${API_URL}/api/gmail/labels`, { withCredentials: true }),
          axios.get(`${API_URL}/api/gmail/messages?label=INBOX&max_results=15`, { withCredentials: true }),
        ]);
        if (!mounted) return;
        const inbox = (labelsR.data || []).find(l => l.id === 'INBOX');
        const accurate = inbox?.messages_unread || 0;
        const msgs = (msgsR.data?.messages || []).filter(m => m.unread);

        if (initialLoadRef.current) {
          // First fetch since mount: seed seen-set, do not notify
          msgs.forEach(m => seenIdsRef.current.add(m.id));
          writeSeen(seenIdsRef.current);
          initialLoadRef.current = false;
        } else {
          const fresh = msgs.filter(m => !seenIdsRef.current.has(m.id));
          fresh.slice(0, 3).forEach(fireNewEmail);
          fresh.forEach(m => seenIdsRef.current.add(m.id));
          if (fresh.length > 0) writeSeen(seenIdsRef.current);
        }
        setUnreadCount(accurate);
      } catch {
        // Likely OAuth not connected or transient failure — keep last known count
      }
    };

    poll();
    const t = setInterval(poll, POLL_MS);

    // Re-poll when tab regains focus (catches new emails received while tab was background)
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      mounted = false;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);

  return unreadCount;
};
